import React from 'react'
import { COLUMNS_IDS, handlePercentCell, LOCAL_DEPARTMENTS_COUNT } from './utils'
import { BaseEntityForTable } from 'utils/types'
import { Box, Stack, SxProps } from '@mui/material'
import Tooltip from 'components/Tooltip'
import AntdTooltip from 'UI/Tooltip/Tooltip'
import NoData from 'components/NoData/NoData'
import ReportedStatus from 'components/ReportedStatus/ReportedStatus'
import Income from 'components/Income'
import { NOT_AVAILABLE } from 'utils/constants'
import BusinessDataSources from 'components/BusinessDataSources/BusinessDataSources'
import { extractRootDomainNoExt } from 'containers/NFT/specs'
import { isNumber } from 'lodash'
import { formatNumber, formattedDate } from 'utils/formatters'
import styles from './columns.scss'
import moment from 'moment'
import { ArrowDownOutlined, ArrowUpOutlined, InfoCircleOutlined, UserOutlined } from '@ant-design/icons'
import CopyToClipboard from 'UI/CopyToClipboard/CopyToClipboard'
import TableTextCard from 'components/common/TableTextCard'
import { businessTypeUrls } from 'containers/VerticalPage/cdtfa/OverviewTab/utils'
import {
  colorsByValues,
  EcomExtendedSellerRegStatusEnum,
  EcommerceExtendedOverviewEnums,
} from 'containers/EcommerceExtendedTablePage/constants'
import ColorStatusCell from 'components/common/ColorStatusCell'
import { ColumnsRecord } from './types'
import { dotColors, getMagnitudeConfig, getRiskConfig } from 'components/ecommerceExtended/EcommerceExtendedTable/util'
import RegistryNumberColumn from 'components/ecommerceExtended/EcommerceExtendedTable/RegistryNumberColumn'
import { getAbbreviatedNumberString } from 'components/Income/Income'
import { RevenueSourceSummary } from 'store/verticals/types'
import { RevenueSource } from 'store/verticals/types'
import { originalSourceToImageMap } from 'components/BusinessSource/specs'
import { summaryAccessor } from 'utils/tableUtils'
import BusinessName from 'components/BusinessName/BusinessName'
import businessService from 'services/business'
import { startCase } from 'lodash'
import BusinessZoneCount from 'components/BusinessZoneCount/BusinessZoneCount'
import BusinessIcon from 'components/Icon'

const imagePlaceholderIconUrl = require('../../icons/image-placeholder.svg')
const ethIconUrl = require('../../icons/eth.svg')

const ColoredDot = ({ sx }: { sx: SxProps }) => {
  return <Box sx={{ width: '10px', height: '10px', borderRadius: '50%', ...sx }}></Box>
}

const getTotalRevenueSources = (revenueSources: RevenueSource[]) => {
  const counts: RevenueSourceSummary[] = []
  revenueSources?.forEach(item => {
    const found = counts.find(c => c.platform === item.platform)
    if (found) {
      found.count++
    } else {
      counts.push({ platform: item.platform, count: 1 })
    }
  })
  return counts
}

interface RegistryAddress {
  value: string
}

interface RegistryEntry {
  addresses: RegistryAddress[]
}

const columns: ColumnsRecord = {
  [COLUMNS_IDS.profileNames]: {
    title: 'Profile Names',
    key: COLUMNS_IDS.profileNames,
    width: 380,
    minWidth: 380,
    fixed: 'left',
    render: (item: BaseEntityForTable) => {
      const profileNames = item?.revenueSources?.reduce((currentNames, { profileName }) => {
        if (!currentNames.includes(profileName)) {
          currentNames.push(profileName)
        }
        return currentNames
      }, [] as string[])
      return (
        <Box>
          <Stack direction='row'>
            {profileNames?.length === 0 && <></>}
            {profileNames?.length === 1 && profileNames[0]}
            {profileNames?.length > 1 && (
              <Tooltip title={profileNames.join(', ')}>
                <Stack direction='row' spacing={1}>
                  {profileNames[0]}
                  {` +${profileNames.length - 1}`}
                </Stack>
              </Tooltip>
            )}
          </Stack>
        </Box>
      )
    },
  },
  [COLUMNS_IDS.verifiedName]: {
    title: 'Verified Name',
    width: 200,
    minWidth: 200,
    key: COLUMNS_IDS.verifiedName,
    sorter: true,
    render: item => (item?.entityDetails?.name ? item.entityDetails.name : 'N/A'),
  },
  [COLUMNS_IDS.tin]: {
    title: 'TIN',
    width: 180,
    minWidth: 180,
    key: COLUMNS_IDS.tin,
    render: item => item?.entityDetails?.tin || 'TIN not found',
  },
  [COLUMNS_IDS.reportStatus]: {
    title: 'Report Status',
    width: 200,
    minWidth: 200,
    key: COLUMNS_IDS.reportStatus,
    render: item =>
      item?.entityDetails?.reportedStatus ? (
        <ReportedStatus reportedStatus={item.entityDetails.reportedStatus} id={item.id} />
      ) : (
        <NoData />
      ),
  },
  [COLUMNS_IDS.unreportedRevenue]: {
    title: 'Unreported Revenue',
    width: 200,
    sorter: true,
    minWidth: 200,
    key: COLUMNS_IDS.unreportedRevenue,
    render: item =>
      item?.overviewDetails?.taxGap ? <Income value={item?.overviewDetails?.taxGap} /> : 'Issue not found',
  },
  [COLUMNS_IDS.reported]: {
    title: 'Reported',
    width: 200,
    sorter: true,
    minWidth: 200,
    key: COLUMNS_IDS.reported,
    render: item =>
      item?.overviewDetails?.reportedRevenue ? (
        <Income value={item?.overviewDetails?.reportedRevenue} />
      ) : (
        NOT_AVAILABLE
      ),
  },
  [COLUMNS_IDS.totalRevenue]: {
    title: 'Gain/Loss (USD)',
    width: 200,
    sorter: true,
    minWidth: 200,
    key: COLUMNS_IDS.totalRevenue,
    render: item => (item?.totals?.totalRevenue ? <Income value={item?.totals?.totalRevenue} /> : NOT_AVAILABLE),
  },
  [COLUMNS_IDS.grossProfit]: {
    title: 'Gross Profit',
    sorter: true,
    width: 200,
    minWidth: 200,
    key: COLUMNS_IDS.grossProfit,
    render: item => (item?.totals?.grossProfit ? <Income value={item?.totals?.grossProfit} /> : NOT_AVAILABLE),
  },
  [COLUMNS_IDS.totalSales]: {
    title: 'Total Sales',
    sorter: true,
    width: 200,
    minWidth: 200,
    key: COLUMNS_IDS.totalSales,
    render: item => (item?.totals?.documentedSales ? <Income value={item.totals?.documentedSales} /> : NOT_AVAILABLE),
  },
  [COLUMNS_IDS.totalRoyalties]: {
    title: 'Total Royalties',
    sorter: true,
    width: 200,
    minWidth: 200,
    key: COLUMNS_IDS.totalRoyalties,
    render: item => {
      if (item?.totals?.documentedRoyalties) {
        return <Income value={item?.totals?.documentedRoyalties} />
      }
      if (item?.overviewDetails?.activityType === 'NFT Creator and Trader') {
        return '0'
      }
      return NOT_AVAILABLE
    },
  },
  [COLUMNS_IDS.totalBuys]: {
    title: 'Total Buys',
    width: 200,
    sorter: true,
    minWidth: 200,
    key: COLUMNS_IDS.totalBuys,
    render: item => (item?.totals?.documentedBuys ? <Income value={item?.totals?.documentedBuys} /> : NOT_AVAILABLE),
  },
  [COLUMNS_IDS.dataSources]: {
    title: 'Data Sources',
    width: 300,
    minWidth: 300,
    key: COLUMNS_IDS.dataSources,
    render: (item: BaseEntityForTable) => {
      const { contactInfo, entityDetails, revenueSources, overviewDetails } = item
      const platforms: string[] = []

      if (contactInfo && entityDetails && revenueSources) {
        revenueSources.forEach(({ platform }) => {
          if (!platforms.includes(platform?.toLowerCase())) {
            platforms.push(platform?.toLowerCase())
          }
        })
        const dataSources = Object.keys(contactInfo.weblinks || {})
        dataSources?.forEach((source: string) => {
          if (!platforms.includes(source?.toLowerCase())) {
            platforms.push(source?.toLowerCase())
          }
        })
        entityDetails.profileUrls?.forEach((source: string) => {
          const platform = extractRootDomainNoExt(source)
          if (!platforms.includes(platform?.toLowerCase())) {
            platforms.push(platform?.toLowerCase())
          }
        })

        return <BusinessDataSources dataSources={platforms?.sort() || []} />
      } else if (overviewDetails?.dataSources) {
        return (
          <Stack gap={1} direction='row'>
            {[...new Set(overviewDetails.dataSources)]
              .sort()
              .map(
                platform =>
                  businessTypeUrls[platform as keyof typeof businessTypeUrls] && (
                    <Box
                      key={platform}
                      component='img'
                      src={businessTypeUrls[platform as keyof typeof businessTypeUrls]}
                      width={20}
                      height={20}
                      alt={platform}
                    />
                  ),
              )}
          </Stack>
        )
      } else if (item?.detailsSources?.length && item?.detailsSources?.length > 0) {
        const sourcePlatforms = item.detailsSources.map(source => source.platform)
        return (
          <Stack gap={1} direction='row'>
            {[...new Set(sourcePlatforms)]
              .sort()
              .map(
                platform =>
                  businessTypeUrls[platform as keyof typeof businessTypeUrls] && (
                    <Box
                      key={platform}
                      component='img'
                      src={businessTypeUrls[platform as keyof typeof businessTypeUrls]}
                      width={20}
                      height={20}
                      alt={platform}
                    />
                  ),
              )}
          </Stack>
        )
      } else return NOT_AVAILABLE
    },
  },
  [COLUMNS_IDS.activityType]: {
    title: 'Activity Type',
    width: 200,
    minWidth: 200,
    key: COLUMNS_IDS.activityType,
    render: item => (item?.overviewDetails?.activityType ? item?.overviewDetails.activityType : ''),
  },
  [COLUMNS_IDS.firstActivity]: {
    title: 'First Activity',
    width: 180,
    sorter: true,
    minWidth: 180,
    key: COLUMNS_IDS.firstActivity,
    render: item =>
      isNumber(item?.totals?.firstActivity) || /^\d{4}$/.test(item?.totals?.firstActivity)
        ? item?.totals?.firstActivity
        : formattedDate(item?.totals?.firstActivity, 'MMM. YYYY'),
  },
  [COLUMNS_IDS.caseId]: {
    title: 'Case ID',
    width: 160,
    sorter: true,
    minWidth: 160,
    key: COLUMNS_IDS.caseId,
    render: item => (item?.caseId ? item?.caseId : ''),
  },
  [COLUMNS_IDS.locations]: {
    title: 'Locations',
    width: 200,
    sorter: true,
    minWidth: 200,
    key: COLUMNS_IDS.locations,
    render: item => {
      if (item?.overviewDetails?.docRefArs?.length === 1) return item.overviewDetails.docRefArs[0]
      if (item?.overviewDetails?.docRefArs && item?.overviewDetails?.docRefArs?.length > 1) {
        const docRefArs = item?.overviewDetails?.docRefArs

        return (
          <Tooltip
            html={
              <div>
                {item?.overviewDetails?.docRefArs?.map((text: string) => (
                  <React.Fragment key={text}>
                    {text}
                    <br />
                  </React.Fragment>
                ))}
              </div>
            }
          >
            <>{`${docRefArs[0]} +${docRefArs.length - 1} more`}</>
          </Tooltip>
        )
      }
      return item?.entityDetails?.location
    },
  },
  [COLUMNS_IDS.cryptoTokenName]: {
    title: 'Token Name',
    key: COLUMNS_IDS.cryptoTokenName,
    width: 150,
    minWidth: 150,
    fixed: 'left',
    render: item => (
      <div className={styles.tokenNameContainer}>
        <img
          src={item.activityPhoto || imagePlaceholderIconUrl}
          alt={item.tokenName}
          style={{ width: 'px', height: '22px', objectFit: 'contain' }}
        />
        <div>
          {item?.tokenName?.length > 15 ? (
            <AntdTooltip
              title={item.tokenName}
              placement='top'
              color='#383838'
              classNames={{ root: styles.tooltipRoot, body: styles.tooltipBody }}
              mainTextContentClassName={styles.tooltipMainTextContent}
            >
              {`${item.tokenName.slice(0, 15)}...`}
            </AntdTooltip>
          ) : (
            item.tokenName || 'N/A'
          )}
          <div>
            {item?.tokenId && item?.tokenId !== 'None' && item?.tokenId !== 'none' && (
              <>
                #
                {item.tokenId.length > 15 ? (
                  <AntdTooltip
                    title={item.tokenId}
                    placement='top'
                    color='#383838'
                    classNames={{ root: styles.tooltipRoot, body: styles.tooltipBody }}
                    mainTextContentClassName={styles.tooltipMainTextContent}
                  >
                    {`${item.tokenId.slice(0, 15)}...`}
                  </AntdTooltip>
                ) : (
                  item.tokenId
                )}
              </>
            )}
          </div>
        </div>
      </div>
    ),
  },
  [COLUMNS_IDS.cryptoAmount]: {
    title: 'Amount',
    width: 100,
    minWidth: 100,
    key: COLUMNS_IDS.cryptoAmount,
    render: item => item.amount,
  },
  [COLUMNS_IDS.cryptoDateAndTime]: {
    title: 'Date and time',
    width: 160,
    minWidth: 160,
    key: COLUMNS_IDS.cryptoDateAndTime,
    render: item => {
      return (
        <div>
          <div>{moment(item.activityDate, 'YYYY/MM/DD HH:mm').format('MMM DD YYYY')}</div>
          <div>{moment(item.activityDate, 'YYYY/MM/DD HH:mm').format('hh:mm A')}</div>
        </div>
      )
    },
  },
  [COLUMNS_IDS.cryptoGainLossUSD]: {
    title: 'Gain/Loss USD',
    width: 160,
    minWidth: 160,
    key: COLUMNS_IDS.cryptoGainLossUSD,
    render: item => (
      <div className={styles.gainLossUSDcontainer}>
        {typeof item.revenue === 'number' && item.revenue > 0 && <ArrowUpOutlined style={{ color: '#52C41A' }} />}
        {typeof item.revenue === 'number' && item.revenue < 0 && <ArrowDownOutlined style={{ color: '#FF4D4F' }} />}
        <div>{typeof item.revenue === 'number' ? <Income value={item.revenue} /> : 'N/A'}</div>
      </div>
    ),
  },
  [COLUMNS_IDS.cryptoActivityFrom]: {
    title: 'From',
    width: 160,
    minWidth: 160,
    key: COLUMNS_IDS.cryptoActivityFrom,
    render: item =>
      item.activityFrom ? (
        <CopyToClipboard text={item.activityFrom}>
          {item?.additionalData?.name && (item.direction === 'OUT' || item.direction === 'SELF_TRANSFER') && (
            <AntdTooltip
              title={item.additionalData.name}
              placement='top'
              color='#383838'
              classNames={{ root: styles.tooltipRoot, body: styles.tooltipBody }}
              mainTextContentClassName={styles.tooltipMainTextContent}
            >
              <UserOutlined style={{ color: '#1890FF' }} />
            </AntdTooltip>
          )}
        </CopyToClipboard>
      ) : (
        'N/A'
      ),
  },
  [COLUMNS_IDS.cryptoActivityTo]: {
    title: 'To',
    width: 160,
    minWidth: 160,
    key: COLUMNS_IDS.cryptoActivityTo,
    render: item =>
      item.activityTo ? (
        <CopyToClipboard text={item.activityTo}>
          {item?.additionalData?.name && (item.direction === 'IN' || item.direction === 'SELF_TRANSFER') && (
            <AntdTooltip
              title={item.additionalData.name}
              placement='top'
              color='#383838'
              classNames={{ root: styles.tooltipRoot, body: styles.tooltipBody }}
              mainTextContentClassName={styles.tooltipMainTextContent}
            >
              <UserOutlined style={{ color: '#1890FF' }} />
            </AntdTooltip>
          )}
        </CopyToClipboard>
      ) : (
        'N/A'
      ),
  },
  [COLUMNS_IDS.cryptoDirection]: {
    title: (
      <>
        <span>Direction</span>
        <AntdTooltip
          title="'In' = received funds; 'Out' = sent funds. Used to classify inflows and outflows"
          placement='bottom'
          color='#fff'
        >
          <InfoCircleOutlined style={{ color: '#00000073', marginLeft: '6px', transform: 'translateY(1px)' }} />
        </AntdTooltip>
      </>
    ),
    width: 160,
    minWidth: 160,
    key: COLUMNS_IDS.cryptoDirection,
    render: item =>
      item.direction
        ? item.direction
            .split('_')
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
        : 'N/A',
  },
  [COLUMNS_IDS.cryptoTransactionHash]: {
    title: (
      <>
        <span>Transaction Hash</span>
        <AntdTooltip
          title='Unique ID for the transaction. Click to view full details on the blockchain.'
          placement='bottom'
          color='#fff'
        >
          <InfoCircleOutlined style={{ color: '#00000073', marginLeft: '6px', transform: 'translateY(1px)' }} />
        </AntdTooltip>
      </>
    ),
    width: 170,
    minWidth: 170,
    key: COLUMNS_IDS.cryptoTransactionHash,
    render: item =>
      item.transactionHash ? <CopyToClipboard text={item.transactionHash} url={item.activityUrl} /> : 'N/A',
  },
  [COLUMNS_IDS.cryptoCostBasisUSD]: {
    title: 'Cost Basis (USD)',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.cryptoCostBasisUSD,
    render: item => (typeof item.costBasis === 'number' ? <Income value={item.costBasis} /> : 'N/A'),
  },
  [COLUMNS_IDS.cryptoSellValueUSD]: {
    title: 'Sell Value (USD)',
    width: 215,
    minWidth: 215,
    key: COLUMNS_IDS.cryptoSellValueUSD,
    render: item => (typeof item.value === 'number' ? <Income value={item.value} /> : 'N/A'),
  },
  [COLUMNS_IDS.cryptoChain]: {
    title: 'Chain',
    width: 85,
    minWidth: 85,
    key: COLUMNS_IDS.cryptoChain,
    render: () => <img src={ethIconUrl} alt='eth' />,
  },
  [COLUMNS_IDS.cryptoActivityType]: {
    title: 'Activity Type',
    width: 130,
    minWidth: 130,
    key: COLUMNS_IDS.cryptoActivityType,
    render: item => item.activityType,
  },
  [COLUMNS_IDS.cryptoCostBasisItem]: {
    title: 'Item',
    width: 135,
    minWidth: 135,
    fixed: 'left',
    key: COLUMNS_IDS.cryptoCostBasisItem,
    render: item => `${item.tokenName} ${item?.tokenId ? `#${item.tokenId}` : ''}`,
  },
  [COLUMNS_IDS.cryptoCostBasisDateAndTime]: {
    title: 'Date and Time',
    width: 160,
    minWidth: 160,
    key: COLUMNS_IDS.cryptoCostBasisDateAndTime,
    render: item => moment(item.activityDate, 'YYYY/MM/DD HH:mm').format('MMM DD YYYY hh:mm A'),
  },
  [COLUMNS_IDS.costBasisAllocation]: {
    title: 'Cost Basis Allocation',
    width: 180,
    minWidth: 180,
    key: COLUMNS_IDS.costBasisAllocation,
    render: item => (typeof item.allocated === 'number' ? item.allocated : 'N/A'),
  },
  [COLUMNS_IDS.cryptoTokenType]: {
    title: 'Token Type',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.cryptoTokenType,
    render: item => item.tokenType || 'N/A',
  },
  [COLUMNS_IDS.cryptoPurchaseValue]: {
    title: 'Purchase value',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.cryptoPurchaseValue,
    render: item => (typeof item.value === 'number' ? <Income value={item.value} /> : 'N/A'),
  },
  [COLUMNS_IDS.companyIndustry]: {
    title: 'Industry',
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.companyIndustry,
    render: item => item?.overviewDetails?.industry || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.companyFounded]: {
    title: 'Founded',
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.companyFounded,
    render: item => item?.entityDetails?.founded || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.companyLocations]: {
    title: 'Locations',
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.companyLocations,
    render: item => (Array.isArray(item?.totals?.jurisdictions) ? item?.totals?.jurisdictions?.length : NOT_AVAILABLE),
  },
  [COLUMNS_IDS.companyEmployeesLocal]: {
    title: 'Employees Local',
    width: 175,
    sorter: true,
    minWidth: 175,
    key: COLUMNS_IDS.companyEmployeesLocal,
    render: item => item?.totals?.employeesLocal ?? NOT_AVAILABLE,
  },
  [COLUMNS_IDS.companyEmployeesLocalPercent]: {
    title: 'Employees Local (%)',
    width: 195,
    sorter: true,
    minWidth: 195,
    key: COLUMNS_IDS.companyEmployeesLocalPercent,
    render: item => handlePercentCell(item?.totals?.totalLocalPercent),
  },
  [COLUMNS_IDS.companyEmployeesAbroad]: {
    title: 'Employees non-local',
    width: 195,
    sorter: true,
    minWidth: 195,
    key: COLUMNS_IDS.companyEmployeesAbroad,
    render: item => item?.totals?.employeesAbroad ?? NOT_AVAILABLE,
  },
  [COLUMNS_IDS.companyEmployeesAbroadPercent]: {
    title: 'Employees non-local (%)',
    width: 220,
    sorter: true,
    minWidth: 220,
    key: COLUMNS_IDS.companyEmployeesAbroadPercent,
    render: item => handlePercentCell(item?.totals?.employeesAbroadPercent),
  },
  [COLUMNS_IDS.companyExecutivesLocal]: {
    title: 'Executives Local',
    width: 175,
    sorter: true,
    minWidth: 175,
    key: COLUMNS_IDS.companyExecutivesLocal,
    render: item => item?.totals?.executivesLocal ?? NOT_AVAILABLE,
  },
  [COLUMNS_IDS.companyExecutivesLocalPercent]: {
    title: 'Executives Local (%)',
    width: 195,
    sorter: true,
    minWidth: 195,
    key: COLUMNS_IDS.companyExecutivesLocalPercent,
    render: item => handlePercentCell(item?.totals?.executivesLocalPercent),
  },
  [COLUMNS_IDS.nonRnDLocal]: {
    title: 'Non-R&D Local',
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.nonRnDLocal,
    render: item => item?.totals?.nonRnDLocal ?? NOT_AVAILABLE,
  },
  [COLUMNS_IDS.nonRnDLocalPercent]: {
    title: 'Non-R&D Local (%)',
    width: 185,
    sorter: true,
    minWidth: 185,
    key: COLUMNS_IDS.nonRnDLocalPercent,
    render: item => handlePercentCell(item?.totals?.nonRnDLocalPercent),
  },
  [COLUMNS_IDS.rndLocal]: {
    title: 'R&D Local',
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.rndLocal,
    render: item => item?.totals?.rnDLocal ?? NOT_AVAILABLE,
  },
  [COLUMNS_IDS.rndLocalPercent]: {
    title: 'R&D Local (%)',
    width: 185,
    sorter: true,
    minWidth: 185,
    key: COLUMNS_IDS.rndLocalPercent,
    render: item => handlePercentCell(item?.totals?.rnDLocalPercent),
  },
  [COLUMNS_IDS.localDepartments]: {
    title: 'Local Departments',
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.localDepartments,
    render: rowData => {
      const localDepartments = [...new Set(rowData?.totals?.localDepartments || [])]

      return (
        <Stack direction='row' sx={{ justifyContent: 'flex-start', gap: '10px', flexWrap: 'wrap' }}>
          {!Array.isArray(localDepartments) || !localDepartments?.length ? (
            NOT_AVAILABLE
          ) : localDepartments.length > LOCAL_DEPARTMENTS_COUNT ? (
            <Tooltip
              theme='transparent'
              html={
                <Box>
                  {localDepartments.length >= LOCAL_DEPARTMENTS_COUNT
                    ? localDepartments.slice(LOCAL_DEPARTMENTS_COUNT).map(dep => (
                        <Box mr='10px' sx={{ wordBreak: 'keep-all' }} key={dep as string}>
                          {dep as string}
                        </Box>
                      ))
                    : ''}
                </Box>
              }
            >
              <Stack direction='row' alignItems='center' mr='10px' width='300px'>
                {localDepartments.slice(0, LOCAL_DEPARTMENTS_COUNT).map(dep => (
                  <TableTextCard text={dep as string} sx={{ mr: '10px' }} key={dep as string} />
                ))}
                <Box sx={{ wordBreak: 'keep-all' }}>
                  {localDepartments.length > LOCAL_DEPARTMENTS_COUNT &&
                    `+${localDepartments.length - LOCAL_DEPARTMENTS_COUNT} more`}
                </Box>
              </Stack>
            </Tooltip>
          ) : (
            <Box mr='10px'>
              {localDepartments.map(dep => (
                <TableTextCard text={dep as string} sx={{ mr: '10px' }} key={dep as string} />
              ))}
            </Box>
          )}
        </Stack>
      )
    },
  },
  [COLUMNS_IDS.companyType]: {
    title: 'Company Type',
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.companyType,
    render: item => item?.overviewDetails?.companyType || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.ecommerceDocumentedRevenue]: {
    title: 'Documented Revenue',
    width: 205,
    sorter: true,
    minWidth: 205,
    key: COLUMNS_IDS.ecommerceDocumentedRevenue,
    render: item =>
      item?.totals?.documentedRevenue ? <Income value={item?.totals?.documentedRevenue} /> : NOT_AVAILABLE,
  },
  [COLUMNS_IDS.ecommerceEstimatedRevenue]: {
    title: 'Estimated Revenue',
    width: 200,
    sorter: true,
    minWidth: 200,
    key: COLUMNS_IDS.ecommerceEstimatedRevenue,
    render: item =>
      item?.totals?.estimatedRevenue ? <Income value={item?.totals?.estimatedRevenue} /> : NOT_AVAILABLE,
  },
  [COLUMNS_IDS.ecommerceCollectsSalesTax]: {
    title: 'Collects sales tax',
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.ecommerceCollectsSalesTax,
    render: item => {
      const value = item?.entityDetails?.collectsSalesTax
      if (value === null || value === undefined) {
        return NOT_AVAILABLE
      }
      if (typeof value === 'boolean') {
        return value ? 'True' : 'False'
      }
      if (typeof value === 'string') {
        const normalizedValue = value.toLowerCase().trim()
        return normalizedValue === 'true' ? 'True' : 'False'
      }
      return NOT_AVAILABLE
    },
  },
  [COLUMNS_IDS.ecommerceTotalSoldItems]: {
    title: 'Total Sold Items',
    width: 185,
    sorter: true,
    minWidth: 185,
    key: COLUMNS_IDS.ecommerceTotalSoldItems,
    render: item =>
      formatNumber(item?.totals?.totalItemsSold, null, {
        style: 'decimal',
      }),
  },
  [COLUMNS_IDS.ecommerceTotalReviews]: {
    title: 'Total Reviews',
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.ecommerceTotalReviews,
    render: item =>
      formatNumber(item?.totals?.totalReviews, null, {
        style: 'decimal',
      }),
  },
  [COLUMNS_IDS.ecommerceTaxCollection]: {
    title: 'Tax Collection',
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.ecommerceTaxCollection,
    render: item => {
      const collectionStatus = item?.overviewDetails?.cartCheckStatus as keyof typeof EcommerceExtendedOverviewEnums

      if (!collectionStatus || !EcommerceExtendedOverviewEnums?.[collectionStatus]) {
        return NOT_AVAILABLE
      }

      const color = colorsByValues.taxCollectionStatus?.[collectionStatus]
      const label = EcommerceExtendedOverviewEnums[collectionStatus]
      return <ColorStatusCell label={label} color={color} />
    },
  },
  [COLUMNS_IDS.ecommerceSellerRegStatus]: {
    title: 'Seller Reg. Status',
    width: 195,
    sorter: true,
    minWidth: 195,
    key: COLUMNS_IDS.ecommerceSellerRegStatus,
    render: item => {
      const regStatus = item?.overviewDetails?.risk?.registrationStatus as keyof typeof EcomExtendedSellerRegStatusEnum
      if (!regStatus) {
        return item?.overviewDetails?.risk?.registrationStatus || NOT_AVAILABLE
      }

      const color = colorsByValues.sellerRegistryStatus?.[regStatus]
      const label = EcomExtendedSellerRegStatusEnum?.[regStatus]
      return <ColorStatusCell label={label} color={color} />
    },
  },
  [COLUMNS_IDS.ecommerceRiskScore]: {
    title: (
      <>
        <span>IVIX Risk</span>
        <AntdTooltip
          title="IVIX risk score reflects potential for non-compliance, based on a proprietary model using OSINT data, behavior patterns, and anomalies. 'Critical' denotes the highest concern."
          placement='bottom'
          color='#fff'
        >
          <InfoCircleOutlined style={{ color: '#B8AFAF', marginLeft: '6px' }} />
        </AntdTooltip>
      </>
    ),
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.ecommerceRiskScore,
    render: item => {
      if (item?.overviewDetails?.risk?.score === undefined || item?.overviewDetails?.risk?.score === null) {
        return NOT_AVAILABLE
      }

      const score = item.overviewDetails.risk.score
      const riskConfig = getRiskConfig(score)

      return (
        <Stack direction='row' alignItems='center' gap={1}>
          <ColoredDot sx={{ bgcolor: riskConfig?.color }} />
          <div>{riskConfig?.label || NOT_AVAILABLE}</div>
        </Stack>
      )
    },
  },
  [COLUMNS_IDS.ecommerceMagnitudeScore]: {
    title: (
      <>
        <span>IVIX Magnitude</span>
        <AntdTooltip
          title="Magnitude reflects the estimated scale of the seller's operations, based on selective indicators."
          placement='bottom'
          color='#fff'
        >
          <InfoCircleOutlined style={{ color: '#B8AFAF', marginLeft: '6px' }} />
        </AntdTooltip>
      </>
    ),
    width: 175,
    sorter: true,
    minWidth: 175,
    key: COLUMNS_IDS.ecommerceMagnitudeScore,
    render: item => {
      if (item?.overviewDetails?.magnitude?.score === undefined || item?.overviewDetails?.magnitude?.score === null) {
        return NOT_AVAILABLE
      }

      const score = item.overviewDetails.magnitude.score
      const magnitudeConfig = getMagnitudeConfig(score)

      return (
        <Stack direction='row' alignItems='center' gap={1}>
          <ColoredDot sx={{ bgcolor: magnitudeConfig?.color }} />
          <div>{magnitudeConfig?.label || NOT_AVAILABLE}</div>
        </Stack>
      )
    },
  },
  [COLUMNS_IDS.ecommerceRegistry]: {
    title: 'Seller Reg. ID',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.ecommerceRegistry,
    render: item => <RegistryNumberColumn registry={item?.overviewDetails?.registry || []} />,
  },
  [COLUMNS_IDS.ecommerceShopCategories]: {
    title: 'Shop Categories',
    width: 195,
    sorter: true,
    minWidth: 195,
    key: COLUMNS_IDS.ecommerceShopCategories,
    render: item => {
      const categories = item?.overviewDetails?.shopsCategories || []
      const firstCategory = categories[0] || NOT_AVAILABLE
      const remainingCount = categories.length - 1

      return (
        <div>
          {firstCategory}
          {remainingCount > 0 && (
            <Tooltip theme='light' title={categories.slice(1).join(', ')}>
              <span> +{remainingCount}</span>
            </Tooltip>
          )}
        </div>
      )
    },
  },
  [COLUMNS_IDS.ecommerceAddressNexus]: {
    title: (
      <>
        <span>Physical Nexus: Address</span>
        <AntdTooltip
          title='​​Indicates whether the business has a confirmed physical address, which may trigger local tax obligations (i.e., sales tax nexus in some jurisdictions).'
          placement='bottom'
          color='#fff'
        >
          <InfoCircleOutlined style={{ color: '#B8AFAF', marginLeft: '6px' }} />
        </AntdTooltip>
      </>
    ),
    width: 250,
    sorter: true,
    minWidth: 250,
    key: COLUMNS_IDS.ecommerceAddressNexus,
    render: item => {
      const hasAddressNexus = item?.overviewDetails?.risk?.physicalNexus?.business
      const color = hasAddressNexus ? dotColors.green : dotColors.red
      return (
        <Stack direction='row' alignItems='center' gap={1}>
          <ColoredDot sx={{ bgcolor: color }} />
          <div>{hasAddressNexus ? 'True' : 'False'}</div>
        </Stack>
      )
    },
  },
  [COLUMNS_IDS.ecommerceEmployeesNexus]: {
    title: (
      <>
        <span>Physical Nexus: Employees</span>
        <AntdTooltip
          title='Indicates whether the business has local employees, which can also establish a physical nexus for tax purposes.'
          placement='bottom'
          color='#fff'
        >
          <InfoCircleOutlined style={{ color: '#B8AFAF', marginLeft: '6px' }} />
        </AntdTooltip>
      </>
    ),
    width: 270,
    sorter: true,
    minWidth: 270,
    key: COLUMNS_IDS.ecommerceEmployeesNexus,
    render: item => {
      const hasEmployeesNexus = item?.overviewDetails?.risk?.physicalNexus?.employees
      const color = hasEmployeesNexus ? dotColors.green : dotColors.red
      return (
        <Stack direction='row' alignItems='center' gap={1}>
          <ColoredDot sx={{ bgcolor: color }} />
          <div>{hasEmployeesNexus ? 'True' : 'False'}</div>
        </Stack>
      )
    },
  },
  [COLUMNS_IDS.ecommerceNumberOfShops]: {
    title: 'Number of Shops',
    width: 185,
    sorter: true,
    minWidth: 185,
    key: COLUMNS_IDS.ecommerceNumberOfShops,
    render: item => item?.overviewDetails?.magnitude?.numberOfShops || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.ecommerceAddresses]: {
    title: 'Addresses',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.ecommerceAddresses,
    render: item => {
      const registry = item?.overviewDetails?.registry || []
      const addresses = registry.flatMap((reg: RegistryEntry) =>
        reg.addresses.map((addressObj: RegistryAddress) => addressObj.value),
      )
      const firstAddress = addresses[0] || NOT_AVAILABLE
      const remainingCount = addresses.length - 1

      return (
        <div>
          {firstAddress}
          {remainingCount > 0 && (
            <Tooltip
              theme='light'
              html={
                <Box>
                  {addresses.slice(1).map((address: string) => (
                    <Box mr='10px' sx={{ wordBreak: 'keep-all' }} key={address}>
                      {address}
                    </Box>
                  ))}
                </Box>
              }
            >
              <span style={{ fontWeight: 'bold' }}> +{remainingCount}</span>
            </Tooltip>
          )}
        </div>
      )
    },
  },
  [COLUMNS_IDS.websiteRanking]: {
    title: 'Website Ranking',
    width: 185,
    sorter: true,
    minWidth: 185,
    key: COLUMNS_IDS.websiteRanking,
    render: item => item?.overviewDetails?.magnitude?.rank || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.ecommerceNumberOfEmployees]: {
    title: 'Number of Employees',
    width: 200,
    sorter: true,
    minWidth: 200,
    key: COLUMNS_IDS.ecommerceNumberOfEmployees,
    render: item => item?.totals?.numberOfEmployees || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.ecommerceAvgMonthlyCountryTraffic]: {
    title: 'Avg. Monthly Country Traffic',
    width: 245,
    sorter: true,
    minWidth: 245,
    key: COLUMNS_IDS.ecommerceAvgMonthlyCountryTraffic,
    render: item => {
      const averageLocalTrafficMonthly = item?.overviewDetails?.magnitude?.averageLocalTrafficMonthly
      return averageLocalTrafficMonthly ? getAbbreviatedNumberString(averageLocalTrafficMonthly) : NOT_AVAILABLE
    },
  },
  [COLUMNS_IDS.ecommerceAvgMonthlyTraffic]: {
    title: 'Avg. Monthly WW Traffic',
    width: 235,
    sorter: true,
    minWidth: 235,
    key: COLUMNS_IDS.ecommerceAvgMonthlyTraffic,
    render: item => {
      const averageTrafficMonthly = item?.overviewDetails?.magnitude?.averageTrafficMonthly
      return averageTrafficMonthly ? getAbbreviatedNumberString(averageTrafficMonthly) : NOT_AVAILABLE
    },
  },
  [COLUMNS_IDS.influencersTotalMedia]: {
    title: 'Total Posts',
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.influencersTotalMedia,
    render: item =>
      formatNumber(item?.totals?.totalMedia, null, {
        style: 'decimal',
      }),
  },
  [COLUMNS_IDS.influencersTotalFollowers]: {
    title: 'Total Followers',
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.influencersTotalFollowers,
    render: item =>
      formatNumber(item?.totals?.totalFollowers, null, {
        style: 'decimal',
      }),
  },
  [COLUMNS_IDS.activeListings]: {
    title: (
      <>
        <span>Active Listings</span>
        <AntdTooltip
          title='Current number of listings offered by the host that are available for booking.'
          placement='bottom'
          color='#fff'
        >
          <InfoCircleOutlined style={{ color: '#B8AFAF', marginLeft: '6px' }} />
        </AntdTooltip>
      </>
    ),
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.activeListings,
    render: item => item?.totals?.activeListings || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.totalListings]: {
    title: (
      <>
        <span>Total Listings</span>
        <AntdTooltip
          title='Cumulative number of listings ever associated with this host, including inactive or removed listings.'
          placement='bottom'
          color='#fff'
        >
          <InfoCircleOutlined style={{ color: '#B8AFAF', marginLeft: '6px' }} />
        </AntdTooltip>
      </>
    ),
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.totalListings,
    render: item => item?.totals?.totalListings || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.revenueSources]: {
    title: 'Revenue sources',
    width: 185,
    minWidth: 185,
    key: COLUMNS_IDS.revenueSources,
    render: item => (
      <div className={styles.platformIconsContainer}>
        {getTotalRevenueSources(item.revenueSources).map((x, i) => (
          <div key={i} className={styles.tag}>
            <img
              src={originalSourceToImageMap[x?.platform?.toLocaleLowerCase() || 'globe']}
              alt='Platform'
              className={styles.platformIcon}
              style={{ width: '14px', height: '14px' }}
            />
            {x.platform} {x.count > 1 ? `(${x.count})` : ''}
          </div>
        ))}
      </div>
    ),
  },
  [COLUMNS_IDS.serviceProvidersExactRevenue]: {
    title: 'Exact Revenue',
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.serviceProvidersExactRevenue,
    render: item => (item?.totals?.exactRevenue ? <Income value={item?.totals?.exactRevenue} /> : NOT_AVAILABLE),
  },
  [COLUMNS_IDS.offshoreLinkedEntities]: {
    title: 'Linked Entities',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.offshoreLinkedEntities,
    render: item => {
      if (Array.isArray(item?.overviewDetails?.entities) && item?.overviewDetails?.entities.length > 1) {
        return (
          <Tooltip title={item?.overviewDetails?.entities.join(', ')}>
            <Stack direction='row' spacing={1}>
              <div>{item?.overviewDetails?.entities[0]}</div>
              <div>{`+ ${item?.overviewDetails?.entities.length - 1}`}</div>
            </Stack>
          </Tooltip>
        )
      }

      if (Array.isArray(item?.overviewDetails?.entities) && item?.overviewDetails?.entities.length === 1) {
        return <div>{item?.overviewDetails?.entities[0]}</div>
      }

      return NOT_AVAILABLE
    },
  },
  [COLUMNS_IDS.offshoreOfficerRole]: {
    title: 'Officer Role',
    width: 185,
    minWidth: 185,
    key: COLUMNS_IDS.offshoreOfficerRole,
    render: item => {
      if (Array.isArray(item?.overviewDetails?.roles) && item?.overviewDetails?.roles.length > 1) {
        return (
          <Tooltip title={item?.overviewDetails?.roles.join(', ')}>
            <Stack direction='row' alignItems='center' justifyContent='flex-start' spacing={1}>
              <div>{item?.overviewDetails?.roles[0]}</div>
              <div style={{ whiteSpace: 'nowrap' }}>{`+ ${item?.overviewDetails?.roles.length - 1}`}</div>
            </Stack>
          </Tooltip>
        )
      }

      if (Array.isArray(item?.overviewDetails?.roles) && item?.overviewDetails?.roles.length === 1) {
        return <div>{item?.overviewDetails?.roles[0]}</div>
      }

      return NOT_AVAILABLE
    },
  },
  [COLUMNS_IDS.lastActivity]: {
    title: 'Last Activity',
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.lastActivity,
    render: item =>
      item?.overviewDetails?.lastActivity !== null
        ? isNumber(item?.overviewDetails?.lastActivity) || /^\d{4}$/.test(item?.overviewDetails?.lastActivity)
          ? item?.overviewDetails?.lastActivity
          : formattedDate(item?.overviewDetails?.lastActivity, 'MMM. YYYY')
        : 'No last Activity',
  },
  [COLUMNS_IDS.addresses]: {
    title: 'Addresses',
    width: 185,
    minWidth: 185,
    key: COLUMNS_IDS.addresses,
    render: item => {
      if (Array.isArray(item?.contactInfo?.addresses) && item?.contactInfo?.addresses.length > 1) {
        return (
          <Tooltip title={item?.contactInfo?.addresses.join(', ')}>
            <Stack direction='row' spacing={1}>
              <div>{item?.contactInfo?.addresses[0]}</div>
              <Box sx={{ width: '30px', whiteSpace: 'nowrap' }}>
                <div>{`+ ${item?.contactInfo?.addresses.length - 1}`}</div>
              </Box>
            </Stack>
          </Tooltip>
        )
      }

      if (Array.isArray(item?.contactInfo?.addresses) && item?.contactInfo?.addresses.length === 1) {
        return <div>{item?.contactInfo?.addresses[0]}</div>
      }

      return NOT_AVAILABLE
    },
  },
  [COLUMNS_IDS.zone]: {
    title: 'Tax Office: Income Tax Civil',
    width: 220,
    minWidth: 220,
    key: COLUMNS_IDS.zone,
    render: item => item?.zone || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.firstActivityOverview]: {
    title: 'First Activity',
    width: 155,
    sorter: true,
    minWidth: 155,
    key: COLUMNS_IDS.firstActivityOverview,
    render: item =>
      isNumber(item?.overviewDetails?.firstActivity) || /^\d{4}$/.test(item?.overviewDetails?.firstActivity)
        ? item?.overviewDetails?.firstActivity
        : formattedDate(item?.overviewDetails?.firstActivity, 'MMM. YYYY'),
  },
  [COLUMNS_IDS.attributedRevenue]: {
    title: 'Total attributed earnings',
    width: 220,
    minWidth: 220,
    sorter: true,
    key: COLUMNS_IDS.attributedRevenue,
    render: item => {
      if (typeof item?.totals?.attributedRevenue === 'number') {
        return formatNumber(item.totals?.attributedRevenue)
      }

      return NOT_AVAILABLE
    },
  },
  [COLUMNS_IDS.summary]: {
    title: 'Summary',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.summary,
    render: item =>
      summaryAccessor(item?.overviewDetails?.summary || [], [
        'accountNumber',
        'controlledEntity',
        'controlPersonType',
        'bankName',
        'jurisdiction',
      ]),
  },
  [COLUMNS_IDS.controllingEntities]: {
    title: 'Controlling Entities',
    width: 185,
    minWidth: 185,
    key: COLUMNS_IDS.controllingEntities,
    render: item => summaryAccessor(item?.overviewDetails?.summary || [], ['controlledEntity', 'controlPersonType']),
  },
  [COLUMNS_IDS.entityDetailsSiteId]: {
    title: 'Site ID',
    width: 155,
    minWidth: 155,
    sorter: true,
    key: COLUMNS_IDS.entityDetailsSiteId,
    render: item => item?.entityDetails?.siteId || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.entityDetailsNaicsCdtfa]: {
    title: 'Data Science NAICS',
    width: 185,
    minWidth: 185,
    sorter: true,
    key: COLUMNS_IDS.entityDetailsNaicsCdtfa,
    render: item => item?.overviewDetails?.naicsCdtfa || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.entityDetailsNaicsIvix]: {
    title: 'IVIX NAICS',
    width: 155,
    minWidth: 155,
    sorter: true,
    key: COLUMNS_IDS.entityDetailsNaicsIvix,
    render: item =>
      Array.isArray(item?.overviewDetails?.naicsIvix) ? item?.overviewDetails?.naicsIvix?.join(', ') : NOT_AVAILABLE,
  },
  [COLUMNS_IDS.aggregatedTotalsFirstActivity]: {
    title: 'First Activity',
    width: 155,
    minWidth: 155,
    sorter: true,
    key: COLUMNS_IDS.aggregatedTotalsFirstActivity,
    render: item => item?.aggregatedTotals?.firstActivity || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.accountName]: {
    title: 'Account Name',
    width: 155,
    minWidth: 155,
    sorter: true,
    key: COLUMNS_IDS.accountName,
    render: item => item?.overviewDetails?.accountName || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.customerName]: {
    title: 'Customer Name',
    width: 185,
    minWidth: 185,
    sorter: true,
    key: COLUMNS_IDS.customerName,
    render: item => item?.overviewDetails?.customerName || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.accountId]: {
    title: 'Account ID',
    width: 155,
    minWidth: 155,
    sorter: true,
    key: COLUMNS_IDS.accountId,
    render: item => item?.overviewDetails?.accountId || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.qtrYieldIvix]: {
    title: 'TOTAL TAX ADJUSTMENT IVIX (ACCOUNT)',
    width: 350,
    minWidth: 350,
    sorter: true,
    key: COLUMNS_IDS.qtrYieldIvix,
    render: item => item?.aggregatedTotals?.qtrYieldIvix || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.salesTaxesDue]: {
    title: 'TOTAL TAX DUE (ACCOUNT)',
    width: 250,
    minWidth: 250,
    sorter: true,
    key: COLUMNS_IDS.salesTaxesDue,
    render: item => item?.aggregatedTotals?.salesTaxesDue || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.businessType]: {
    title: 'Business Type',
    width: 155,
    minWidth: 155,
    sorter: true,
    key: COLUMNS_IDS.businessType,
    render: item => item?.overviewDetails?.accountType || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.siteStartDate]: {
    title: 'Commence',
    width: 155,
    minWidth: 155,
    sorter: true,
    key: COLUMNS_IDS.siteStartDate,
    render: item =>
      item?.aggregatedTotals?.siteStartDate ? formattedDate(item?.aggregatedTotals?.siteStartDate) : NOT_AVAILABLE,
  },
  [COLUMNS_IDS.siteEndDate]: {
    title: 'Ceased',
    width: 155,
    minWidth: 155,
    sorter: true,
    key: COLUMNS_IDS.siteEndDate,
    render: item =>
      item?.aggregatedTotals?.siteEndDate ? formattedDate(item?.aggregatedTotals?.siteEndDate) : NOT_AVAILABLE,
  },
  [COLUMNS_IDS.numYearsWithQtrYield]: {
    title: 'YEARS UNDER-REPORTING',
    width: 240,
    minWidth: 240,
    sorter: true,
    key: COLUMNS_IDS.numYearsWithQtrYield,
    render: item => item?.aggregatedTotals?.numYearsWithQtrYield || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.totalAdjustedOfTotalDue]: {
    title: 'PERCENTAGE OF TAX ADJUSTED',
    width: 280,
    minWidth: 280,
    key: COLUMNS_IDS.totalAdjustedOfTotalDue,
    render: rowData => {
      const totals = rowData?.aggregatedTotals

      if (!totals || typeof totals?.totalAdjustedOfTotalDue !== 'number') {
        return NOT_AVAILABLE
      }

      const percentage =
        totals?.totalAdjustedOfTotalDue !== 0
          ? (totals?.totalAdjustedOfTotalDue * 100).toFixed(2)
          : totals?.totalAdjustedOfTotalDue * 100

      return `${percentage}%`
    },
  },
  [COLUMNS_IDS.siteStatus]: {
    title: 'Site Status',
    width: 155,
    minWidth: 155,
    sorter: true,
    key: COLUMNS_IDS.siteStatus,
    render: item => item?.aggregatedTotals?.siteStatus || NOT_AVAILABLE,
  },
  [COLUMNS_IDS.businessName]: {
    title: 'Business Name',
    width: 155,
    minWidth: 155,
    fixed: 'left',
    key: COLUMNS_IDS.businessName,
    render: item => <BusinessName alignedLeft business={item} />,
  },
  [COLUMNS_IDS.businessReportedStatus]: {
    title: 'Reported Status',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.businessReportedStatus,
    render: item => <ReportedStatus reportedStatus={item?.reportedStatus} id={item?.id} />,
  },
  [COLUMNS_IDS.businessDataSources]: {
    title: 'Data Sources',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.businessDataSources,
    render: item => {
      const dataSources = businessService.dataSources(item)
      return <BusinessDataSources dataSources={dataSources} />
    },
  },
  [COLUMNS_IDS.businessStructure]: {
    title: 'Business Form',
    width: 175,
    minWidth: 175,
    key: COLUMNS_IDS.businessStructure,
    render: item => item?.businessStructure || '',
  },
  [COLUMNS_IDS.businessIndustry]: {
    title: 'Industry',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.businessIndustry,
    render: item => {
      const { businessType } = item
      if (!businessType || (Array.isArray(businessType) && !businessType.length)) return null

      const businessTypes = Array.isArray(businessType) ? businessType : [businessType]

      const [firstType] = businessTypes

      return (
        <span className={styles.multiple}>
          <span className={styles.businessType}>
            <Tooltip title={firstType}>{firstType}</Tooltip>
          </span>
          <span>
            {businessTypes.length > 1 && (
              <Tooltip title={businessTypes.slice(1).map(startCase).join(', ')}>
                <span className={styles.moreItems}>{`+ ${businessTypes.length - 1}`}</span>
              </Tooltip>
            )}
          </span>
        </span>
      )
    },
  },
  [COLUMNS_IDS.businessAddress]: {
    title: 'Address',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.businessAddress,
    render: item => {
      const { businessAddresses } = item

      if (!businessAddresses || !businessAddresses.length) return null

      const [firstAddress] = businessAddresses

      let tooltip = businessAddresses.slice(1, 4).join(',  ')
      if (businessAddresses.length > 4) tooltip += ` , and ${businessAddresses.length - 4} more addresses...`

      return (
        <span className={styles.multiple}>
          <div className={styles.firstValue}>
            <Tooltip title={firstAddress}>{firstAddress}</Tooltip>
          </div>
          {businessAddresses.length > 1 && (
            <Tooltip title={tooltip}>
              <span className={styles.moreItems}>{`+ ${businessAddresses.length - 1}`}</span>
            </Tooltip>
          )}
        </span>
      )
    },
  },
  [COLUMNS_IDS.businessOwners]: {
    title: 'Owners',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.businessOwners,
    render: item => {
      const { businessOwner } = item

      if (!businessOwner || !businessOwner.length) return null

      const ownerNames = businessOwner.map((owner: { name: string }) => owner.name)
      const [firstOwner] = ownerNames

      return (
        <span className={styles.multiple}>
          {firstOwner}
          {ownerNames.length > 1 && (
            <Tooltip title={ownerNames.join(', ')}>
              <span className={styles.moreItems}>{` +${ownerNames.length - 1}`}</span>
            </Tooltip>
          )}
        </span>
      )
    },
  },
  [COLUMNS_IDS.businessTin]: {
    title: 'TIN',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.businessTin,
    render: item => item?.tin || '',
  },
  [COLUMNS_IDS.businessLocations]: {
    title: 'Locations',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.businessLocations,
    render: item => <BusinessZoneCount type='location' counts={item.listings} />,
  },
  [COLUMNS_IDS.businessEmployees]: {
    title: 'Employees',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.businessEmployees,
    render: item => <BusinessZoneCount type='employee' counts={item.employees} />,
  },
  [COLUMNS_IDS.businessMarketingEfforts]: {
    title: 'Marketing Spending',
    width: 185,
    minWidth: 185,
    key: COLUMNS_IDS.businessMarketingEfforts,
    render: item => <BusinessZoneCount type='marketing' counts={item.marketing} />,
  },
  [COLUMNS_IDS.businessDocumentedIncome]: {
    title: 'Documented',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.businessDocumentedIncome,
    render: item => {
      const { documentedIncome = 0 } = item

      if (!documentedIncome) return <NoData />
      return <Income isFormatted={false} value={documentedIncome || 0} />
    },
  },
  [COLUMNS_IDS.businessReportedIncome]: {
    title: 'Reported',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.businessReportedIncome,
    render: item => {
      const { reportedIncome = 0 } = item

      if (!reportedIncome) return <NoData />
      return <Income isFormatted={false} value={reportedIncome || 0} />
    },
  },
  [COLUMNS_IDS.businessNumberOfReviews]: {
    title: 'Number of Reviews',
    width: 185,
    minWidth: 185,
    key: COLUMNS_IDS.businessNumberOfReviews,
    render: item => (typeof item?.reviewsCount === 'number' ? item?.reviewsCount : 'N/A'),
  },
  [COLUMNS_IDS.businessNumberOfVisitors]: {
    title: 'Number of Visitors',
    width: 185,
    minWidth: 185,
    key: COLUMNS_IDS.businessNumberOfVisitors,
    render: item => (typeof item?.visitorsCount === 'number' ? item?.visitorsCount : 'N/A'),
  },
  [COLUMNS_IDS.businessUntaxedIncome]: {
    title: 'Untaxed Revenue',
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.businessUntaxedIncome,
    render: item => {
      const { untaxedIncome = 0 } = item

      return (
        <>
          <BusinessIcon size='small' name={`indicator-collection-${item.easeOfCollection}`} />
          <Income isFormatted={false} value={untaxedIncome || 0} />
        </>
      )
    },
  },
  [COLUMNS_IDS.hostName]: {
    title: (
      <>
        <span>Host Name</span>
        <AntdTooltip title='Names used by entity/individual online' placement='bottom' color='#fff'>
          <InfoCircleOutlined style={{ color: '#B8AFAF', marginLeft: '6px' }} />
        </AntdTooltip>
      </>
    ),
    width: 155,
    minWidth: 155,
    key: COLUMNS_IDS.hostName,
    render: item => item?.totals?.hostName || NOT_AVAILABLE,
  },
}

export default columns
